import { FC, useEffect } from 'react';
import { AsideMenu } from './AsideMenu';
import { CollapsedMenu } from './menu/CollapsedMenu';
import { PanelLeftClose } from 'lucide-react';
import { useSidebar } from './menu/SidebarContext';

const AsideDefault: FC = () => {
  const { collapsed, setCollapsed, toggle } = useSidebar();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const mobileQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 1023px)')
      : null;

    const updateBodyScroll = () => {
      const isMobile = mobileQuery ? mobileQuery.matches : window.innerWidth < 1024;
      document.body.style.overflow = !collapsed && isMobile ? 'hidden' : previousOverflow;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const isMobile = mobileQuery ? mobileQuery.matches : window.innerWidth < 1024;

      if (event.key === 'Escape' && !collapsed && isMobile) {
        setCollapsed(true);
      }
    };

    updateBodyScroll();
    window.addEventListener('keydown', handleKeyDown);

    if (mobileQuery && typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateBodyScroll);
    } else if (mobileQuery) {
      mobileQuery.addListener(updateBodyScroll);
    } else {
      window.addEventListener('resize', updateBodyScroll);
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);

      if (mobileQuery && typeof mobileQuery.removeEventListener === 'function') {
        mobileQuery.removeEventListener('change', updateBodyScroll);
      } else if (mobileQuery) {
        mobileQuery.removeListener(updateBodyScroll);
      } else {
        window.removeEventListener('resize', updateBodyScroll);
      }
    };
  }, [collapsed, setCollapsed]);

  return (
    <>
      <aside
        id="site-main-menu"
        className={`
          fixed top-0 left-0 h-screen z-40
          border-r border-white/5
          flex flex-col
          transition-all duration-300 ease-out
          shadow-2xl shadow-slate-950/30 lg:shadow-none
          ${collapsed
            ? 'w-0 -translate-x-full lg:w-[72px] lg:translate-x-0'
            : 'w-[min(88vw,320px)] translate-x-0 rounded-r-2xl lg:w-[265px] lg:rounded-none'
          }
        `}
        style={{ backgroundColor: '#00335d' }}
        role="navigation"
        aria-label="Menu lateral principal"
      >
        <div className="flex items-center justify-between h-[60px] px-4 flex-shrink-0 pt-5">
          {!collapsed && (
            <div className="flex items-center overflow-hidden transition-opacity duration-200">
              <img
                src="https://interno.compra-agora.com/media/logos/logo-wide.png"
                alt="Compra Agora"
                className="h-14 object-contain"
              />
            </div>
          )}

          {collapsed && (
            <div className="hidden lg:flex items-center justify-center w-full">
              <button
                onClick={toggle}
                className="flex items-center justify-center w-11 h-11 rounded-xl hover:bg-white/10 transition-colors duration-200 cursor-pointer"
                aria-label="Expandir menu"
              >
                <img
                  src="/media/logos/logo-icon.png"
                  alt="Compra Agora"
                  className="h-10 w-10 object-contain"
                />
              </button>
            </div>
          )}

          {!collapsed && (
            <button
              onClick={toggle}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-200 flex-shrink-0 cursor-pointer"
              aria-label="Recolher ou fechar menu"
            >
              <PanelLeftClose size={18} />
            </button>
          )}
        </div>

        {collapsed ? (
          <div className="hidden lg:flex flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll py-3">
            <CollapsedMenu />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll">
            <AsideMenu />
          </div>
        )}
      </aside>

      {!collapsed && (
        <div
          className="fixed inset-0 bg-slate-950/55 z-30 lg:hidden backdrop-blur-[2px] transition-opacity duration-200"
          onClick={() => setCollapsed(true)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export { AsideDefault };
